import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Handle, Position } from "@xyflow/react";
import PaymentConfigService from "../../services/PaymentConfigService";

export const PaymentMessageNode = ({ data, id, selected = false }) => {
  const PAYMENT_SCOPE_ORDER_DETAILS = "order_details";
  const PAYMENT_SCOPE_PAYMENT_LINK = "payment_link";
  const PAYMENT_TYPES = [
    { value: "payment_link", label: "Payment Link" },
    { value: "order_payment", label: "Order Payment" },
    { value: "orka_checkout", label: "Orka Checkout" },
  ];

  const [showMenu, setShowMenu] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const menuRef = useRef(null);

  const [paymentType, setPaymentType] = useState(data.paymentType || "order_payment");
  const [header, setHeader] = useState(data.header || "");
  const [body, setBody] = useState(data.body || "");
  const [footer, setFooter] = useState(data.footer || "");
  const [paymentItems, setPaymentItems] = useState(
    Array.isArray(data.paymentItems) && data.paymentItems.length > 0
      ? data.paymentItems
      : [{ name: "", amount: "" }]
  );
  const [configurationId, setConfigurationId] = useState(data.configurationId || "");
  const [configurationName, setConfigurationName] = useState(data.configurationName || "");

  const [editPaymentType, setEditPaymentType] = useState(data.paymentType || "order_payment");
  const [editHeader, setEditHeader] = useState(data.header || "");
  const [editBody, setEditBody] = useState(data.body || "");
  const [editFooter, setEditFooter] = useState(data.footer || "");
  const [editPaymentItems, setEditPaymentItems] = useState(
    Array.isArray(data.paymentItems) && data.paymentItems.length > 0
      ? data.paymentItems
      : [{ name: "", amount: "" }]
  );
  const [editConfigurationId, setEditConfigurationId] = useState(data.configurationId || "");
  const [editConfigurationName, setEditConfigurationName] = useState(data.configurationName || "");

  const [configs, setConfigs] = useState([]);
  const [loadingConfigs, setLoadingConfigs] = useState(false);
  const [configError, setConfigError] = useState("");
  const canUseOrkaCheckout = Boolean(data?.hasStoreId || data?.hasActivePaymentLinkConfigurations);
  const canUseNativeOrderPayments = Boolean(
    data?.canUseOrderDetailsPayments && data?.hasActiveOrderDetailsPaymentConfigurations
  );
  const canUsePaymentLinks = Boolean(data?.hasActivePaymentLinkConfigurations);
  const canSkipManualPaymentItems = Boolean(data?.hasCatalogConnected);

  useEffect(() => {
    setPaymentType(data.paymentType || "order_payment");
    setHeader(data.header || "");
    setBody(data.body || "");
    setFooter(data.footer || "");
    setPaymentItems(
      Array.isArray(data.paymentItems) && data.paymentItems.length > 0
        ? data.paymentItems
        : [{ name: "", amount: "" }]
    );
    setConfigurationId(data.configurationId || "");
    setConfigurationName(data.configurationName || "");
  }, [data.paymentType, data.header, data.body, data.footer, data.paymentItems, data.configurationId, data.configurationName]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showMenu]);

  const loadActiveConfigs = async (scope = PAYMENT_SCOPE_ORDER_DETAILS) => {
    try {
      setLoadingConfigs(true);
      const list = await PaymentConfigService.listLocal();
      const active = (list || []).filter(
        (cfg) => String(cfg?.active_status || "").toLowerCase() === "active"
          && String(cfg?.usage_scope || PAYMENT_SCOPE_ORDER_DETAILS).toLowerCase() === scope
      );
      setConfigs(active);
    } catch (e) {
      setConfigs([]);
    } finally {
      setLoadingConfigs(false);
    }
  };

  const openEdit = async () => {
    const type = paymentType || "order_payment";
    setEditPaymentType(type);
    setEditHeader(header || "");
    setEditBody(body || "");
    setEditFooter(footer || "");
    setEditPaymentItems(
      Array.isArray(paymentItems) && paymentItems.length > 0
        ? paymentItems
        : [{ name: "", amount: "" }]
    );
    setEditConfigurationId(configurationId || "");
    setEditConfigurationName(configurationName || "");
    setConfigError("");
    setShowEditPopup(true);
    setShowMenu(false);

    if (!["order_payment", "payment_link"].includes(type)) {
      return;
    }

    await loadActiveConfigs(type === "payment_link" ? PAYMENT_SCOPE_PAYMENT_LINK : PAYMENT_SCOPE_ORDER_DETAILS);
  };

  const handleDelete = () => {
    if (data.onDelete) data.onDelete(id);
    setShowMenu(false);
  };

  const handleSave = () => {
    if (editPaymentType === "orka_checkout" && !canUseOrkaCheckout) {
      setConfigError("Orka Checkout is unavailable because store_id is not configured.");
      return;
    }
    if (editPaymentType === "order_payment" && !canUseNativeOrderPayments) {
      setConfigError("Native Order Payment requires new-number onboarding and an active order details configuration.");
      return;
    }
    if (editPaymentType === "payment_link" && !canUsePaymentLinks) {
      setConfigError("Payment Link requires an active payment-link credential in Settings.");
      return;
    }
    if (editPaymentType !== "orka_checkout" && !canSkipManualPaymentItems) {
      const normalizedItems = (Array.isArray(editPaymentItems) ? editPaymentItems : [])
        .map((item) => ({
          name: String(item?.name || "").trim(),
          amount: String(item?.amount || "").trim(),
        }))
        .filter((item) => item.name || item.amount);
      const hasInvalidItem = normalizedItems.some((item) => {
        const n = Number(item.amount);
        return !item.name || !Number.isFinite(n) || n <= 0;
      });
      if (hasInvalidItem) {
        setConfigError("Each Product or Service Name and Amount must be valid");
        return;
      }
    }
    if (["order_payment", "payment_link"].includes(editPaymentType) && !editConfigurationId) {
      setConfigError("Please select one active payment configuration");
      return;
    }

    if (data.onChangePaymentType) data.onChangePaymentType(id, editPaymentType || "order_payment");
    if (data.onChangeHeader) data.onChangeHeader(id, editHeader || "");
    if (data.onChangeBody) data.onChangeBody(id, editBody || "");
    if (data.onChangeFooter) data.onChangeFooter(id, editFooter || "");
    if (data.onChangePaymentItems) {
      let normalizedItems;
      if (editPaymentType === "orka_checkout") {
        normalizedItems = [];
      } else {
        normalizedItems = (Array.isArray(editPaymentItems) ? editPaymentItems : [])
          .map((item) => ({
            name: String(item?.name || "").trim(),
            amount: String(item?.amount || "").trim(),
          }))
          .filter((item) => item.name && item.amount);
      }
      data.onChangePaymentItems(id, normalizedItems);
    }
    if (data.onChangeConfigurationId) data.onChangeConfigurationId(id, editConfigurationId);
    if (data.onChangeConfigurationName) data.onChangeConfigurationName(id, editConfigurationName || "");

    setPaymentType(editPaymentType || "order_payment");
    setHeader(editHeader || "");
    setBody(editBody || "");
    setFooter(editFooter || "");
    setPaymentItems(
      editPaymentType === "orka_checkout"
        ? []
        : (Array.isArray(editPaymentItems) ? editPaymentItems : [])
    );
    setConfigurationId(editConfigurationId);
    setConfigurationName(editConfigurationName || "");
    setShowEditPopup(false);
  };

  const handleCancel = () => {
    setShowEditPopup(false);
    setShowMenu(false);
  };

  const popup = (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "rgba(0, 0, 0, 0.35)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 10000,
      }}
      onClick={handleCancel}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "10px",
          boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
          width: "560px",
          maxWidth: "90vw",
          maxHeight: "88vh",
          overflow: "auto",
          padding: "16px",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ marginTop: 0, marginBottom: 12 }}>Edit Payment Message</h3>

        <div style={{ marginBottom: 10 }}>
          <label style={{ display: "block", fontSize: 12, marginBottom: 6 }}>
            Payment type
          </label>
          <select
            value={editPaymentType}
            onChange={(e) => {
              const nextType = e.target.value;
              if (nextType === "orka_checkout" && !canUseOrkaCheckout) {
                setConfigError("Configure a store or standalone payment-link credentials to use Orka Checkout.");
                return;
              }
              if (nextType === "order_payment" && !canUseNativeOrderPayments) {
                setConfigError("Native Order Payment requires new-number onboarding and an active order details configuration.");
                return;
              }
              if (nextType === "payment_link" && !canUsePaymentLinks) {
                setConfigError("Add an active payment-link credential in Settings to use this type.");
                return;
              }
              setEditPaymentType(nextType);
              setConfigError("");
              if (!["order_payment", "payment_link"].includes(nextType)) {
                setEditConfigurationId("");
                setEditConfigurationName("");
              } else {
                loadActiveConfigs(nextType === "payment_link" ? PAYMENT_SCOPE_PAYMENT_LINK : PAYMENT_SCOPE_ORDER_DETAILS);
              }
            }}
            style={{ width: "100%", padding: 8, borderRadius: 6, border: "1px solid #ccc" }}
          >
            {PAYMENT_TYPES.map((type) => (
              <option
                key={type.value}
                value={type.value}
                disabled={
                  (type.value === "orka_checkout" && !canUseOrkaCheckout)
                  || (type.value === "order_payment" && !canUseNativeOrderPayments)
                  || (type.value === "payment_link" && !canUsePaymentLinks)
                }
              >
                {type.label}
              </option>
            ))}
          </select>
          {!canUseOrkaCheckout ? (
            <div style={{ color: "#b45309", fontSize: 12, marginTop: 4 }}>
              Orka Checkout disabled: connect a store or add standalone payment-link credentials.
            </div>
          ) : null}
          {!canUseNativeOrderPayments ? (
            <div style={{ color: "#b45309", fontSize: 12, marginTop: 4 }}>
              Native Order Payment disabled: not supported for Business App onboarding or no active order details configuration.
            </div>
          ) : null}
          {!canUsePaymentLinks ? (
            <div style={{ color: "#b45309", fontSize: 12, marginTop: 4 }}>
              Payment Link disabled: no active payment-link credential found.
            </div>
          ) : null}
        </div>

        <div style={{ marginBottom: 10 }}>
          <label style={{ display: "block", fontSize: 12, marginBottom: 6 }}>
            Header (optional)
          </label>
          <input
            type="text"
            value={editHeader}
            onChange={(e) => setEditHeader(e.target.value)}
            maxLength={128}
            style={{ width: "100%", padding: 8, borderRadius: 6, border: "1px solid #ccc" }}
          />
        </div>

        <div style={{ marginBottom: 10 }}>
          <label style={{ display: "block", fontSize: 12, marginBottom: 6 }}>
            Body (optional)
          </label>
          <textarea
            value={editBody}
            onChange={(e) => setEditBody(e.target.value)}
            rows={4}
            maxLength={1024}
            style={{
              width: "100%",
              padding: 8,
              borderRadius: 6,
              border: "1px solid #ccc",
              resize: "vertical",
            }}
          />
        </div>

        <div style={{ marginBottom: 10 }}>
          <label style={{ display: "block", fontSize: 12, marginBottom: 6 }}>
            Footer (optional)
          </label>
          <input
            type="text"
            value={editFooter}
            onChange={(e) => setEditFooter(e.target.value)}
            maxLength={128}
            style={{ width: "100%", padding: 8, borderRadius: 6, border: "1px solid #ccc" }}
          />
        </div>

        {editPaymentType !== "orka_checkout" ? (
          <div style={{ marginBottom: 10 }}>
            <label style={{ display: "block", fontSize: 12, marginBottom: 8 }}>
              Product or Service Name with Amount
              {canSkipManualPaymentItems ? (
                <span style={{ fontWeight: 400, color: "#6b7280" }}>
                  {" "}
                  (optional — catalog cart supplies line items for checkout)
                </span>
              ) : null}
            </label>
            {(Array.isArray(editPaymentItems) ? editPaymentItems : []).map((item, index) => (
              <div key={`itm-${index}`} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center" }}>
                <input
                  type="text"
                  placeholder="Product or Service Name"
                  value={item?.name || ""}
                  onChange={(e) => {
                    const next = [...editPaymentItems];
                    next[index] = { ...next[index], name: e.target.value };
                    setEditPaymentItems(next);
                    setConfigError("");
                  }}
                  maxLength={128}
                  style={{ flex: 1, padding: 8, borderRadius: 6, border: "1px solid #ccc" }}
                />
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Amount"
                  value={item?.amount || ""}
                  onChange={(e) => {
                    const next = [...editPaymentItems];
                    next[index] = { ...next[index], amount: e.target.value };
                    setEditPaymentItems(next);
                    setConfigError("");
                  }}
                  style={{ width: 130, padding: 8, borderRadius: 6, border: "1px solid #ccc" }}
                />
                <button
                  type="button"
                  onClick={() => {
                    const next = editPaymentItems.filter((_, i) => i !== index);
                    setEditPaymentItems(next.length ? next : [{ name: "", amount: "" }]);
                  }}
                  style={{ padding: "6px 8px" }}
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => setEditPaymentItems([...(editPaymentItems || []), { name: "", amount: "" }])}
              style={{ padding: "6px 10px" }}
            >
              + Add Product
            </button>
          </div>
        ) : null}

        {["order_payment", "payment_link"].includes(editPaymentType) ? (
          <div style={{ marginBottom: 10 }}>
            <label style={{ display: "block", fontSize: 12, marginBottom: 6 }}>
              {editPaymentType === "payment_link" ? "Payment-link credential" : "Native order details configuration"}
            </label>
            <select
              value={editConfigurationId}
              onChange={(e) => {
                const selected = configs.find((cfg) => String(cfg.id) === String(e.target.value));
                setEditConfigurationId(e.target.value);
                setEditConfigurationName(selected?.configuration_name || "");
                setConfigError("");
              }}
              style={{ width: "100%", padding: 8, borderRadius: 6, border: "1px solid #ccc" }}
            >
              <option value="">
                {loadingConfigs ? "Loading active configurations..." : "Select active configuration"}
              </option>
              {configs.map((cfg) => (
                <option key={cfg.id} value={cfg.id}>
                  {cfg.configuration_name} ({cfg.id})
                </option>
              ))}
            </select>
            {configError ? (
              <div style={{ color: "#d32f2f", fontSize: 12, marginTop: 4 }}>{configError}</div>
            ) : null}
          </div>
        ) : null}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button type="button" onClick={handleCancel} style={{ padding: "8px 12px" }}>
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            style={{
              padding: "8px 12px",
              background: "#1976d2",
              color: "#fff",
              border: "none",
              borderRadius: 6,
            }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div
        style={{
          background: selected ? "#f8f9fa" : "#f1f3f4",
          border: `1px solid ${selected ? "#1976d2" : "#e0e0e0"}`,
          borderRadius: 6,
          padding: 8,
          width: 190,
          minHeight: 82,
          boxShadow: selected
            ? "0 4px 8px rgba(25, 118, 210, 0.3)"
            : "0 2px 4px rgba(0,0,0,0.1)",
          position: "relative",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 10,
            right: 8,
            cursor: "pointer",
            fontSize: 16,
            color: selected ? "#1976d2" : "#6c757d",
            fontWeight: "bold",
          }}
          onClick={() => setShowMenu(!showMenu)}
        >
          ⋮
        </div>

        {showMenu && (
          <div
            ref={menuRef}
            style={{
              position: "absolute",
              top: 24,
              right: 8,
              background: "white",
              border: "1px solid #ddd",
              borderRadius: 4,
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              zIndex: 2,
            }}
          >
            <div
              style={{ padding: "8px 12px", cursor: "pointer", fontSize: 12, borderBottom: "1px solid #eee" }}
              onClick={openEdit}
            >
              ✏️ Edit
            </div>
            <div style={{ padding: "8px 12px", cursor: "pointer", fontSize: 12 }} onClick={handleDelete}>
              🗑️ Delete
            </div>
          </div>
        )}

        <Handle type="target" position={Position.Left} style={{ background: selected ? "#1976d2" : "#6c757d" }} />
        <Handle type="source" position={Position.Right} id={`${id}-main`} style={{ background: selected ? "#1976d2" : "#6c757d" }} />

        <div style={{ textAlign: "left" }}>
          <div style={{ fontWeight: "bold", color: selected ? "#1976d2" : "#6c757d", marginBottom: 6, fontSize: 11 }}>
            Payment Message
          </div>
          <div style={{ fontSize: 10, color: "#4b5563", marginBottom: 4 }}>
            Type: {PAYMENT_TYPES.find((t) => t.value === paymentType)?.label || "Order Payment"}
          </div>
          {header ? (
            <div style={{ fontSize: 10, color: "#6b7280", marginBottom: 2, whiteSpace: "pre-wrap" }}>
              H: {header.slice(0, 70)}
            </div>
          ) : null}
          <div style={{ fontSize: 12, color: "#444", marginBottom: 6, whiteSpace: "pre-wrap" }}>
            {(body || "Click Edit to configure payment message").slice(0, 90)}
          </div>
          {footer ? (
            <div style={{ fontSize: 10, color: "#6b7280", marginBottom: 6, whiteSpace: "pre-wrap" }}>
              F: {footer.slice(0, 70)}
            </div>
          ) : null}
          {paymentType !== "orka_checkout" && Array.isArray(paymentItems) && paymentItems.filter((i) => i?.name && i?.amount).length > 0 ? (
            <div style={{ fontSize: 10, color: "#6b7280", marginBottom: 6 }}>
              Items: {paymentItems.filter((i) => i?.name && i?.amount).length} | Total: {paymentItems.reduce((sum, i) => sum + (Number(i?.amount || 0) || 0), 0)}
            </div>
          ) : null}
          {paymentType !== "orka_checkout" && canSkipManualPaymentItems
          && (!Array.isArray(paymentItems) || paymentItems.filter((i) => i?.name && i?.amount).length === 0) ? (
            <div style={{ fontSize: 10, color: "#166534", marginBottom: 6 }}>
              Manual line items optional — use catalog flow for cart totals
            </div>
          ) : null}
          {["order_payment", "payment_link"].includes(paymentType) ? (configurationId ? (
            <div style={{ fontSize: 10, color: "#166534", background: "#dcfce7", border: "1px solid #86efac", borderRadius: 6, padding: "4px 6px" }}>
              {configurationName ? `${configurationName}` : "Configuration"} ({configurationId})
            </div>
          ) : (
            <div style={{ fontSize: 10, color: "#b91c1c", background: "#fee2e2", border: "1px solid #fecaca", borderRadius: 6, padding: "4px 6px" }}>
              Select an active payment configuration in Edit
            </div>
          )) : null}
        </div>
      </div>
      {showEditPopup ? createPortal(popup, document.body) : null}
    </>
  );
};

